/**
 * Conditional hook wrappers for side effects using conditionalHooksFactory.
 *
 * When the 'platform_editor_isolate_side_effects_experiment' feature flag is enabled:
 * - These hooks return no-ops (empty functions/undefined)
 * - The side effects are handled by FullPageEditorSideEffects component instead
 *
 * When the feature flag is disabled:
 * - These hooks execute normally (original behavior)
 * - All side effects remain in FullPageEditorComponent
 */

import { useEffect } from 'react';

import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset.ts';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';
import { usePluginStateEffect as _usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';

import { expValEquals, expVal } from '@confluence/feature-experiments';
import { useObjectSidebarActions } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { useProcessedCommentsDataActions } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { useMentionsNotificationsActions } from '@confluence/mentions-notifications/entry-points/useMentionsNotifications.ts';
import { useInlineCommentsDispatchContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';

// Import all the original hooks
import {
	useAnnotations as _useAnnotations,
	useAnnotationsDispatch as _useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { usePageNativeEmbedClient as _usePageNativeEmbedClient } from '@confluence/embedded-confluence-allowed-features/entry-points/usePageNativeEmbedClient.ts';
import { useSpaceKeyShortcutOverride as _useSpaceKeyShortcutOverride } from '@confluence/blank-page-quick-actions/entry-points/useSpaceKeyShortcutOverride.ts';
import { useExposeGoogleMeetScreenShare as _useExposeGoogleMeetScreenShare } from '@confluence/embedded-google-meet/entry-points/useExposeGoogleMeetScreenShare.ts';
import { useScrollOnUrlChange as _useScrollOnUrlChange } from '@confluence/editor-features/entry-points/useScrollOnUrlChange.ts';
import { usePrintCoverInjector as _usePrintCoverInjector } from '@confluence/pdf-print-utils/entry-points/coverInjector.ts';
import { useEditorInlineCommentsPubSub as _useEditorInlineCommentsPubSub } from '@confluence/editor-features/entry-points/useEditorInlineCommentsPubSub.ts';
import { useUnreadCommentsSetup as _useUnreadCommentsSetup } from '@confluence/unread-comments/entry-points/useUnreadCommentsSetup.ts';
import { useConnecting as _useConnecting } from '@confluence/native-collab/entry-points/useConnecting.ts';
import { useEditorAIReplacingTitle as _useEditorAIReplacingTitle } from '@confluence/editor-features/entry-points/useEditorAIReplacingTitle.ts';
import { useSendContentAgeAnalytics as _useSendContentAgeAnalytics } from '@confluence/editor-features/entry-points/useSendContentAgeAnalytics.ts';
import { useDisabledAnalytics as _useDisabledAnalytics } from '@confluence/editor-features/entry-points/useDisabledAnalytics.ts';
import { useSchedulePublishFlagsWatcher as _useSchedulePublishFlagsWatcher } from '@confluence/editor-schedule-publish/entry-points/useSchedulePublishFlagsWatcher.ts';

import { useFullPageAutoFocus as _useFullPageAutoFocus } from './useFullPageAutoFocus.ts';
import { useFocusedComment as _useFocusedComment } from './useFocusedComment.ts';
import { useFullPageEditorLoomInsertion as _useFullPageEditorLoomInsertion } from './useFullPageEditorLoomInsertion.ts';
import { useEditorCollabConnectivitySync as _useEditorCollabConnectivitySync } from './useEditorCollabConnectivitySync.ts';

/**
 * usePageNativeEmbedClient - Side effect to setup native embed client
 */
export const usePageNativeEmbedClient = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	() => {
		// Flag enabled: no-op
	},
	() => {
		// Flag disabled: run original hook
		_usePageNativeEmbedClient();
	},
);

/**
 * useSpaceKeyShortcutOverride - Side effect for CWR space key shortcut
 */
export const useSpaceKeyShortcutOverride = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_props: Parameters<typeof _useSpaceKeyShortcutOverride>[0]) => {
		// Flag enabled: no-op
	},
	(props: Parameters<typeof _useSpaceKeyShortcutOverride>[0]) => {
		// Flag disabled: run original hook
		_useSpaceKeyShortcutOverride(props);
	},
);

/**
 * useExposeGoogleMeetScreenShare - Side effect to expose Google Meet screen share
 */
export const useExposeGoogleMeetScreenShare = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	() => {
		// Flag enabled: no-op
	},
	() => {
		// Flag disabled: run original hook
		_useExposeGoogleMeetScreenShare();
	},
);

/**
 * useScrollOnUrlChange - Side effect for scroll on URL change
 */
export const useScrollOnUrlChange = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(..._args: Parameters<typeof _useScrollOnUrlChange>) => {
		// Flag enabled: no-op
	},
	(...args: Parameters<typeof _useScrollOnUrlChange>) => {
		// Flag disabled: run original hook
		_useScrollOnUrlChange(...args);
	},
);

/**
 * usePrintCoverInjector - Side effect for print cover injection
 */
export const usePrintCoverInjector = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(..._args: Parameters<typeof _usePrintCoverInjector>) => {
		// Flag enabled: no-op
	},
	(...args: Parameters<typeof _usePrintCoverInjector>) => {
		// Flag disabled: run original hook
		_usePrintCoverInjector(...args);
	},
);

/**
 * useFocusedComment - Side effect for focused comment handling
 */
export const useFocusedComment = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_props: Parameters<typeof _useFocusedComment>[0]) => {
		// Flag enabled: no-op
	},
	(props: Parameters<typeof _useFocusedComment>[0]) => {
		// Flag disabled: run original hook
		_useFocusedComment(props);
	},
);

/**
 * useEditorInlineCommentsPubSub - Side effect for inline comments PubSub
 */
export const useEditorInlineCommentsPubSub = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_props: Parameters<typeof _useEditorInlineCommentsPubSub>[0]) => {
		// Flag enabled: no-op
	},
	(props: Parameters<typeof _useEditorInlineCommentsPubSub>[0]) => {
		// Flag disabled: run original hook
		return _useEditorInlineCommentsPubSub(props);
	},
);

/**
 * useAnnotations - Side effect for inline comments annotations context
 */
export const useAnnotations = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	() => {
		// Flag enabled: no-op — return empty shape matching useAnnotations return type
		return { storedCommentData: undefined } as ReturnType<typeof _useAnnotations>;
	},
	() => {
		// Flag disabled: run original hook
		return _useAnnotations();
	},
);

/**
 * useAnnotationsDispatch - Side effect for inline comments annotations dispatch context
 */
export const useAnnotationsDispatch = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	() => {
		// Flag enabled: no-op — return empty shape matching useAnnotationsDispatch return type
		return { setStoredCommentData: undefined } as unknown as ReturnType<
			typeof _useAnnotationsDispatch
		>;
	},
	() => {
		// Flag disabled: run original hook
		return _useAnnotationsDispatch();
	},
);

/**
 * useUnreadCommentsSetup - Side effect for unread comments setup
 */
export const useUnreadCommentsSetup = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_props: Parameters<typeof _useUnreadCommentsSetup>[0]) => {
		// Flag enabled: no-op
	},
	(props: Parameters<typeof _useUnreadCommentsSetup>[0]) => {
		// Flag disabled: run original hook
		_useUnreadCommentsSetup(props);
	},
);

/**
 * useConnecting - Side effect for native collab connecting state
 */
export const useConnecting = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_provider: Parameters<typeof _useConnecting>[0]) => {
		// Flag enabled: no-op
	},
	(provider: Parameters<typeof _useConnecting>[0]) => {
		// Flag disabled: run original hook
		_useConnecting(provider);
	},
);

/**
 * useFullPageAutoFocus - Side effect for full page auto focus
 */
export const useFullPageAutoFocus = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_isLivePage: Parameters<typeof _useFullPageAutoFocus>[0]) => {
		// Flag enabled: no-op
	},
	(isLivePage: Parameters<typeof _useFullPageAutoFocus>[0]) => {
		// Flag disabled: run original hook
		_useFullPageAutoFocus(isLivePage);
	},
);

/**
 * useEditorAIReplacingTitle - Side effect for AI replacing title
 */
export const useEditorAIReplacingTitle = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_props: Parameters<typeof _useEditorAIReplacingTitle>[0]) => {
		// Flag enabled: no-op
	},
	(props: Parameters<typeof _useEditorAIReplacingTitle>[0]) => {
		// Flag disabled: run original hook
		_useEditorAIReplacingTitle(props);
	},
);

/**
 * useSendContentAgeAnalytics - Side effect for content age analytics
 */
export const useSendContentAgeAnalytics = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_props: Parameters<typeof _useSendContentAgeAnalytics>[0]) => {
		// Flag enabled: no-op
	},
	(props: Parameters<typeof _useSendContentAgeAnalytics>[0]) => {
		// Flag disabled: run original hook
		_useSendContentAgeAnalytics(props);
	},
);

/**
 * useDisabledAnalytics - Side effect for disabled analytics
 */
export const useDisabledAnalytics = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_props: Parameters<typeof _useDisabledAnalytics>[0]) => {
		// Flag enabled: no-op
	},
	(props: Parameters<typeof _useDisabledAnalytics>[0]) => {
		// Flag disabled: run original hook
		_useDisabledAnalytics(props);
	},
);

/**
 * useSchedulePublishFlagsWatcher - Side effect for schedule publish flags watcher
 */
export const useSchedulePublishFlagsWatcher = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_props: Parameters<typeof _useSchedulePublishFlagsWatcher>[0]) => {
		// Flag enabled: no-op
	},
	(props: Parameters<typeof _useSchedulePublishFlagsWatcher>[0]) => {
		// Flag disabled: run original hook
		_useSchedulePublishFlagsWatcher(props);
	},
);

/**
 * useFullPageEditorLoomInsertion - Side effect for Loom video insertion
 */
export const useFullPageEditorLoomInsertion = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_api: Parameters<typeof _useFullPageEditorLoomInsertion>[0]) => {
		// Flag enabled: no-op
	},
	(api: Parameters<typeof _useFullPageEditorLoomInsertion>[0]) => {
		// Flag disabled: run original hook
		_useFullPageEditorLoomInsertion(api);
	},
);

/**
 * useEditorCollabConnectivitySync - Side effect for collab connectivity sync
 */
export const useEditorCollabConnectivitySync = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_props: Parameters<typeof _useEditorCollabConnectivitySync>[0]) => {
		// Flag enabled: no-op
	},
	(props: Parameters<typeof _useEditorCollabConnectivitySync>[0]) => {
		// Flag disabled: run original hook
		_useEditorCollabConnectivitySync(props);
	},
);

/**
 * useConditionalPluginStateEffect - Side effect for annotation plugin state listening
 * Conditionally wraps usePluginStateEffect based on feature flag
 */
export const useConditionalPluginStateEffect: (
	api: FullPageEditorPresetAPI | undefined,
	keys: string[],
	callback: (state: Record<string, unknown>) => void,
) => void = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(
		_api: FullPageEditorPresetAPI | undefined,
		_keys: string[],
		_callback: (state: Record<string, unknown>) => void,
	) => {
		// Flag enabled: no-op
	},
	(
		api: FullPageEditorPresetAPI | undefined,
		keys: string[],
		callback: (state: Record<string, unknown>) => void,
	) => {
		// Flag disabled: run original hook
		_usePluginStateEffect(api as any, keys, callback);
	},
);

/**
 * useConditionalHideObjectSidebarEffect - Side effect for hiding object sidebar on content change
 * Note: This is now a no-op wrapper since the hook and effect are fully in SideEffects component
 */
export const useConditionalHideObjectSidebarEffect = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_contentId: string) => {
		// Flag enabled: no-op (handled in SideEffects component)
	},
	(contentId: string) => {
		// Flag disabled: run original effect with hook
		const { hideObjectSidebar } = useObjectSidebarActions();
		useEffect(() => {
			void hideObjectSidebar();
		}, [contentId, hideObjectSidebar]);
	},
);

/**
 * useConditionalCommentsCleanupEffect - Side effect for cleaning up comments on unmount/content change
 * Note: This is now a no-op wrapper since the hooks and effect are fully in SideEffects component
 */
export const useConditionalCommentsCleanupEffect = conditionalHooksFactory(
	() => expValEquals('platform_editor_isolate_side_effects_experiment', 'isEnabled', true),
	(_contentId: string, _resetInitialDataLoadedMap: () => void, _clearCommentsData: () => void) => {
		// Flag enabled: no-op (handled in SideEffects component)
	},
	(contentId: string, resetInitialDataLoadedMap: () => void, clearCommentsData: () => void) => {
		// Flag disabled: run original effect with hooks
		const { clearProcessedData } = useProcessedCommentsDataActions();
		const { clearMentionsFlags } = useMentionsNotificationsActions();
		const { setActiveHighlight } = useInlineCommentsDispatchContext();

		useEffect(() => {
			return () => {
				resetInitialDataLoadedMap();
				clearCommentsData();
				if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					clearProcessedData();
				}
				clearMentionsFlags();
				setActiveHighlight(null);
			};
		}, [
			contentId,
			resetInitialDataLoadedMap,
			clearCommentsData,
			clearProcessedData,
			clearMentionsFlags,
			setActiveHighlight,
		]);
	},
);
